import React, { useCallback } from 'react';

import Button from '@atlaskit/button';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { useDataContext } from '../../context/DataContext.tsx';
import { useSurfaceContext } from '../../context/SurfaceContext.tsx';
import { resolveDynamicValue } from '../../engine/dynamicResolver.ts';
import { executeFunctionCall } from '../../engine/functionExecutor.ts';
import { useChild } from '../../hooks/useChildren.tsx';
import type { ActionEvent } from '../../types/A2UICommon.types.tsx';
import type { A2UIButtonProps, ButtonVariant } from '../../types/A2UIComponents.types.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';

const variantMap: Record<ButtonVariant, 'primary' | 'subtle'> = {
	primary: 'primary',
	borderless: 'subtle',
};

const styles = cssMap({
	wrapper: {
		display: 'inline-block',
	},
});

type Props = {
	definition: A2UIComponentDefinition;
	visited?: Set<string>;
};

export const A2UIButton = ({ definition, visited }: Props): React.JSX.Element => {
	const def = definition as A2UIButtonProps;
	const child = useChild(def.child, visited);
	const { state, onAction, customFunctions } = useSurfaceContext();
	const { basePath } = useDataContext();
	const { dataModel } = state;

	const appearance = def.variant ? variantMap[def.variant] : 'default';
	const label = def.accessibility?.label;

	const handleClick = useCallback(() => {
		if (!def.action) {
			return;
		}

		const resolverContext = { dataModel, basePath, customFunctions };

		// FunctionCall action — execute client-side
		if ('functionCall' in def.action) {
			const { call, args } = def.action.functionCall;
			const resolvedArgs: Record<string, unknown> = {};
			for (const [key, argValue] of Object.entries(args)) {
				resolvedArgs[key] = resolveDynamicValue(argValue, resolverContext);
			}
			executeFunctionCall(call, resolvedArgs, customFunctions);
			return;
		}

		// Event action — dispatch to host
		if ('event' in def.action) {
			const actionEvent = def.action as ActionEvent;
			const resolvedContext: Record<string, unknown> = {};
			if (actionEvent.context) {
				for (const [key, val] of Object.entries(actionEvent.context)) {
					resolvedContext[key] = resolveDynamicValue(val, resolverContext);
				}
			}
			onAction({
				surfaceId: state.surfaceId!,
				eventName: actionEvent.event,
				context: resolvedContext,
				dataModel: dataModel.snapshot(),
			});
		}
	}, [def.action, dataModel, basePath, customFunctions, onAction, state.surfaceId]);

	return (
		<Box xcss={cx(styles.wrapper)}>
			<Button appearance={appearance} onClick={handleClick} aria-label={label}>
				{child}
			</Button>
		</Box>
	);
};
